import React, { useCallback, useMemo, useState } from 'react';
import Highcharts, { AxisSetExtremesEventCallbackFunction } from 'highcharts';
import 'highcharts/modules/navigator';
import 'highcharts/modules/stock';
import 'highcharts/modules/xrange';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';
import { TimelineExtremes } from '@pages-clean/subject-monitoring/subject-profile/tabs/timeline/shared';

export const STANDALONE_NAVIGATOR_HEIGHT = 82;

const NAVIGATOR_COLORS = {
    MASK_COLOR: '#0070FF1F',
    SCROLLBAR_TRACK_BACKGROUND: '#E0E0E0',
    SCROLLBAR_THUMB_BACKGROUND: '#AEA8A8',
    RANGE_SELECTOR_INPUT_COLOR: '#984063',
    LABEL_COLOR: '#757575',
} as const;

type Props = {
    ref?: React.Ref<HighchartsReactRefObject>;
    chartRefs: (HighchartsReactRefObject | null)[];
    options: Pick<Highcharts.Options, 'xAxis' | 'series'>;
    borderColor: string;
    startingExtremes: TimelineExtremes;
    onExtremesChanged: (extremes: TimelineExtremes) => void;
};

const NavigatorChart = ({ ref, chartRefs, options, borderColor, startingExtremes, onExtremesChanged }: Props) => {
    const { xAxis, series } = options;

    const afterSetExtremes: AxisSetExtremesEventCallbackFunction = useCallback(
        ({ min, max }) => {
            // Get defined charts from refs, get x axes from charts, set extremes of every x-axis
            chartRefs
                .filter((r) => r != null)
                .flatMap((r) => r.chart.axes)
                .filter((a) => a.isXAxis)
                .forEach((a) => {
                    a.setExtremes(min, max);
                });

            onExtremesChanged({ min, max });
        },
        [chartRefs, onExtremesChanged],
    );

    const [borderBottomWidth, setBorderBottomWidth] = useState(0);

    const allOptions: Highcharts.Options = useMemo(() => {
        // Add event listeners and disable visibility
        const mapXAxis = (opts: Highcharts.XAxisOptions): Highcharts.XAxisOptions => ({
            ...opts,
            events: {
                afterSetExtremes,
            },
            visible: false,
        });

        return {
            credits: {
                enabled: false,
            },
            chart: {
                height: STANDALONE_NAVIGATOR_HEIGHT,
                animation: false,
                style: {
                    marginBottom: '-10px',
                },
                events: {
                    redraw() {
                        const chart = this;
                        setBorderBottomWidth(chart.plotWidth);
                    },
                    load() {
                        const chart = this;
                        setBorderBottomWidth(chart.plotWidth);

                        chart.axes
                            .filter((x) => x.isXAxis)
                            .forEach((x) => {
                                x.setExtremes(startingExtremes.min, startingExtremes.max);
                            });
                    },
                },
            },
            exporting: {
                enabled: false,
            },
            // xAxis can be an array...
            xAxis: xAxis != null ? (Array.isArray(xAxis) ? xAxis.map(mapXAxis) : mapXAxis(xAxis)) : undefined,
            yAxis: {
                visible: false,
            },
            navigator: {
                maskFill: NAVIGATOR_COLORS.MASK_COLOR,
                xAxis: {
                    labels: {
                        style: {
                            color: NAVIGATOR_COLORS.LABEL_COLOR,
                        },
                    },
                },
                height: 24,
            },
            rangeSelector: {
                inputStyle: {
                    color: NAVIGATOR_COLORS.RANGE_SELECTOR_INPUT_COLOR,
                },
                buttons: [],
                labelStyle: {
                    display: 'block',
                },
            },
            scrollbar: {
                barBackgroundColor: NAVIGATOR_COLORS.SCROLLBAR_THUMB_BACKGROUND,
                trackBackgroundColor: NAVIGATOR_COLORS.SCROLLBAR_TRACK_BACKGROUND,
                height: 5,
                trackBorderRadius: 2.5,
                barBorderRadius: 2.5,
            },

            series,
        };
    }, [afterSetExtremes, series, startingExtremes.max, startingExtremes.min, xAxis]);

    return (
        <>
            <HighchartsReact ref={ref} highcharts={Highcharts} constructorType="stockChart" options={allOptions} />

            <div
                style={{
                    width: borderBottomWidth + 1,
                    height: 1,
                    backgroundColor: borderColor,
                    marginLeft: -2,
                    justifySelf: 'center',
                }}
            />
        </>
    );
};

export default NavigatorChart;
